import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { AlertTriangle, ChevronLeft, ChevronRight, Clock, CloudOff, Lock, MapPin, Phone, Pill, PlayCircle, RefreshCw, Stethoscope, WifiOff } from 'lucide-react';
import { Packets, PACKETS_CHANGED_EVENT } from '../../services/offline/packetService';
import { Offline } from '../../services/offline/offlineServices';
import OfflineVisitWorkspace from './workspace/OfflineVisitWorkspace';
import SyncCenter from './SyncCenter';
import { useSyncStatus, OPEN_SYNC_CENTER_EVENT } from '../../hooks/useSyncStatus';
import { useTwoPane } from '../../hooks/useTwoPane';
import type { PacketHeaderDto, PatientPacketDto } from '../../dtos/offline/offline.dto';
import type { AuthUser } from '../../types/permissions';

export interface OfflineHomeProps {
  user: AuthUser;
  online: boolean;
  /** Lock now (keys dropped). */
  onLock: () => void;
  /** Back online: try to restore the full app. */
  onReconnect: () => void;
  reconnecting?: boolean;
}

const fullName = (h: PacketHeaderDto) => [h.preferredName || h.firstName, h.lastName].filter(Boolean).join(' ') || 'Patient';

const addressLine = (h: PacketHeaderDto) =>
  h.address ? [h.address.line1, h.address.line2, h.address.city, h.address.state, h.address.zip].filter(Boolean).join(', ') : '';

const timeRange = (start: string, end: string) => {
  const fmt = (iso: string) => new Date(iso).toLocaleString([], { weekday: 'short', hour: 'numeric', minute: '2-digit' });
  return `${fmt(start)} – ${new Date(end).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}`;
};

/** Allergies arrive as strings or { allergen|name|substance, reaction, severity } objects. */
export function allergyLabels(allergies: unknown): string[] {
  if (!Array.isArray(allergies)) return typeof allergies === 'string' && allergies.trim() ? [allergies.trim()] : [];
  return allergies.map((a) => {
    if (typeof a === 'string') return a;
    if (a && typeof a === 'object') {
      const o = a as Record<string, unknown>;
      const name = String(o.allergen || o.name || o.substance || '').trim();
      const extra = [o.reaction, o.severity].filter((v) => typeof v === 'string' && v).join(', ');
      return name ? (extra ? `${name} (${extra})` : name) : '';
    }
    return '';
  }).filter(Boolean);
}

/** Precautions arrive as a string, a string list, or a { key: true } map. */
export function precautionLabels(precautions: unknown): string[] {
  if (!precautions) return [];
  if (typeof precautions === 'string') return [precautions];
  if (Array.isArray(precautions)) return precautions.map(String).filter(Boolean);
  if (typeof precautions === 'object') return Object.entries(precautions as Record<string, unknown>).filter(([, v]) => v === true || (typeof v === 'string' && v)).map(([k, v]) => (typeof v === 'string' ? `${k}: ${v}` : k));
  return [];
}

const Section: React.FC<{ title: string; icon: React.ReactNode; children: React.ReactNode }> = ({ title, icon, children }) => (
  <section className="rounded-xl border border-gray-200 bg-white p-4">
    <h3 className="mb-2 flex items-center gap-2 text-sm font-semibold uppercase tracking-wide text-gray-500">{icon}{title}</h3>
    {children}
  </section>
);

interface PatientDetailProps {
  packet: PatientPacketDto;
  onBack: () => void;
  onStart: (shiftId: string) => void;
  onContinue: (visitId: string) => void;
}

const PatientDetail: React.FC<PatientDetailProps> = ({ packet, onBack, onStart, onContinue }) => {
  const h = packet.header;
  const allergies = allergyLabels(packet.allergies);
  const precautions = precautionLabels(packet.precautions);
  return (
    <div className="space-y-3">
      <button type="button" onClick={onBack} className="flex min-h-[44px] items-center gap-1 text-sm font-medium text-slate-700"><ChevronLeft size={18} />Patients</button>
      <div>
        <h2 className="text-xl font-semibold text-gray-900">{fullName(h)}</h2>
        <p className="text-sm text-gray-600">{[h.dateOfBirth && `DOB ${h.dateOfBirth}`, h.mrn && `MRN ${h.mrn}`, h.gender].filter(Boolean).join(' · ')}</p>
      </div>

      <Section title="Allergies and precautions" icon={<AlertTriangle size={16} />}>
        {packet.allergiesNoneKnown && !allergies.length ? <p className="text-sm text-gray-800">No known allergies</p>
          : allergies.length ? <ul className="list-disc pl-5 text-sm font-medium text-red-800">{allergies.map((a) => <li key={a}>{a}</li>)}</ul>
            : <p className="text-sm text-gray-500">Not recorded</p>}
        {precautions.length > 0 && <ul className="mt-2 list-disc pl-5 text-sm text-amber-900">{precautions.map((p) => <li key={p}>{p}</li>)}</ul>}
      </Section>

      <Section title="Medications" icon={<Pill size={16} />}>
        {packet.medications.length ? (
          <ul className="divide-y divide-gray-100">
            {packet.medications.map((m, i) => (
              <li key={m.medicationId || `${m.name}-${i}`} className="py-2 text-sm">
                <div className="font-medium text-gray-900">{m.name}{m.prn ? ' (PRN)' : ''}</div>
                <div className="text-gray-600">{[m.dose, m.route, m.frequency].filter(Boolean).join(' · ')}</div>
                {m.instructions && <div className="text-gray-500">{m.instructions}</div>}
              </li>
            ))}
          </ul>
        ) : <p className="text-sm text-gray-500">No active medications</p>}
      </Section>

      <Section title="Diagnoses" icon={<Stethoscope size={16} />}>
        {packet.diagnoses.length ? (
          <ul className="space-y-1 text-sm">{packet.diagnoses.map((d) => <li key={d.code}><span className="font-mono text-gray-700">{d.code}</span> {d.description}{d.primary ? ' · primary' : ''}</li>)}</ul>
        ) : <p className="text-sm text-gray-500">None recorded</p>}
      </Section>

      <Section title="Shifts" icon={<Clock size={16} />}>
        {packet.shifts.length ? (
          <ul className="space-y-2 text-sm text-gray-800">
            {packet.shifts.map((s) => (
              <li key={s.shiftId} className="flex flex-wrap items-center gap-2">
                <span className="flex-1">{timeRange(s.start, s.end)}{s.serviceType ? ` · ${s.serviceType}` : ''}</span>
                <button type="button" onClick={() => onStart(s.shiftId)} className="flex min-h-[44px] items-center gap-1 rounded-lg bg-emerald-700 px-3 font-semibold text-white">
                  <PlayCircle size={16} aria-hidden="true" />Start visit
                </button>
              </li>
            ))}
          </ul>
        ) : <p className="text-sm text-gray-500">No shift in the offline window</p>}
        {packet.recentVisits.length > 0 && (
          <>
            <h4 className="mb-1 mt-3 text-xs font-semibold uppercase text-gray-500">Recent visits</h4>
            <ul className="space-y-2 text-sm text-gray-700">
              {packet.recentVisits.map((v) => (
                <li key={v.visitId} className="flex flex-wrap items-center gap-2">
                  <span className="flex-1">{v.visitDate || (v.startTime && new Date(v.startTime).toLocaleDateString())} · {v.discipline} · chart {String(v.chartingStatus || '').toLowerCase() || 'n/a'}</span>
                  {(v.postVisit || v.chartingStatus === 'DRAFT') && (
                    <button type="button" onClick={() => onContinue(v.visitId)} className="min-h-[44px] rounded-lg border border-slate-700 px-3 font-semibold text-slate-800">Continue charting</button>
                  )}
                </li>
              ))}
            </ul>
          </>
        )}
      </Section>

      <Section title="Contacts" icon={<Phone size={16} />}>
        <ul className="space-y-2 text-sm text-gray-800">
          {addressLine(h) && <li className="flex items-start gap-2"><MapPin size={16} className="mt-0.5 shrink-0" />{addressLine(h)}</li>}
          {h.primaryPhone && <li><a className="text-sky-700 underline" href={`tel:${h.primaryPhone}`}>{h.primaryPhone}</a> (home)</li>}
          {h.guardian?.name && <li>{h.guardian.name}{h.guardian.phone && <> · <a className="text-sky-700 underline" href={`tel:${h.guardian.phone}`}>{h.guardian.phone}</a></>} (guardian)</li>}
          {h.emergencyContact?.name && <li>{h.emergencyContact.name}{h.emergencyContact.relationship ? `, ${h.emergencyContact.relationship}` : ''}{h.emergencyContact.phone && <> · <a className="text-sky-700 underline" href={`tel:${h.emergencyContact.phone}`}>{h.emergencyContact.phone}</a></>} (emergency)</li>}
        </ul>
      </Section>
    </div>
  );
};

type HomeView =
  | { kind: 'list' }
  | { kind: 'workspace'; patientId: string; visitId?: string | null; shiftId?: string | null }
  | { kind: 'sync' };

/**
 * Offline home: what an unlocked clinician sees with no connection (Phase 16A, extended in 16C).
 * Downloaded patients, then the Offline Visit Workspace to start a visit from a shift or continue
 * charting a recent visit, and Sync Center. A visit in progress on this device reopens on unlock
 * or reload. Landscape tablets show the patient list and the workspace side by side (R-310).
 */
const OfflineHome: React.FC<OfflineHomeProps> = ({ user, online, onLock, onReconnect, reconnecting }) => {
  const [packets, setPackets] = useState<PatientPacketDto[] | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [syncedAt, setSyncedAt] = useState<string | null>(null);
  const [view, setView] = useState<HomeView>({ kind: 'list' });
  const sync = useSyncStatus();
  const twoPane = useTwoPane(true);

  const load = useCallback(async () => {
    try {
      setPackets(await Packets.list());
      setSyncedAt(await Packets.lastSyncedAt());
    } catch {
      setPackets([]);
    }
  }, []);

  useEffect(() => {
    load();
    window.addEventListener(PACKETS_CHANGED_EVENT, load);
    const openSync = () => setView({ kind: 'sync' });
    window.addEventListener(OPEN_SYNC_CENTER_EVENT, openSync);
    return () => {
      window.removeEventListener(PACKETS_CHANGED_EVENT, load);
      window.removeEventListener(OPEN_SYNC_CENTER_EVENT, openSync);
    };
  }, [load]);

  // Restore (C6): a visit in progress on this device opens again after unlock or reload
  useEffect(() => {
    Offline.visits.getActive().then((active) => {
      if (active) setView({ kind: 'workspace', patientId: active.patientId, visitId: active.visitId });
    }).catch(() => undefined);
  }, []);

  const selected = useMemo(() => packets?.find((p) => p.patientId === selectedId) || null, [packets, selectedId]);
  const openWorkspace = (patientId: string, ids: { visitId?: string; shiftId?: string }) => setView({ kind: 'workspace', patientId, ...ids });
  const backToList = () => { setView({ kind: 'list' }); load(); };

  const list = (
    <>
      {!packets && <p role="status" className="text-sm text-gray-500">Loading…</p>}
      {packets && !selected && (
        packets.length === 0 ? (
          <p className="rounded-xl border border-dashed border-gray-300 p-6 text-center text-sm text-gray-500">No patients are stored on this device. Open My Day while online to download them.</p>
        ) : (
          <ul className="divide-y divide-gray-100 rounded-xl border border-gray-200 bg-white">
            {packets.map((p) => {
              const next = [...p.shifts].sort((a, b) => a.start.localeCompare(b.start))[0];
              return (
                <li key={p.patientId}>
                  <button type="button" onClick={() => setSelectedId(p.patientId)} className="flex min-h-[64px] w-full items-center justify-between gap-3 px-4 py-2 text-left">
                    <span className="min-w-0">
                      <span className="block truncate text-base font-medium text-gray-900">{fullName(p.header)}</span>
                      <span className="block truncate text-xs text-gray-600">{next ? timeRange(next.start, next.end) : 'Recent visit'}{allergyLabels(p.allergies).length ? ' · allergies' : ''}</span>
                    </span>
                    <ChevronRight size={18} className="shrink-0 text-gray-400" />
                  </button>
                </li>
              );
            })}
          </ul>
        )
      )}
      {selected && (
        <PatientDetail
          packet={selected}
          onBack={() => setSelectedId(null)}
          onStart={(shiftId) => openWorkspace(selected.patientId, { shiftId })}
          onContinue={(visitId) => openWorkspace(selected.patientId, { visitId })}
        />
      )}
    </>
  );

  const workspace = view.kind === 'workspace' && (
    <OfflineVisitWorkspace
      key={`${view.patientId}:${view.visitId || view.shiftId || 'new'}`}
      patientId={view.patientId}
      visitId={view.visitId}
      shiftId={view.shiftId}
      onExit={backToList}
      onVisitStarted={(visitId) => setView({ kind: 'workspace', patientId: view.patientId, visitId })}
    />
  );

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="sticky top-0 z-20 flex items-center gap-2 bg-slate-900 px-4 py-3 text-white" style={{ paddingTop: 'max(0.75rem, env(safe-area-inset-top))' }}>
        <WifiOff size={18} aria-hidden="true" />
        <div className="min-w-0 flex-1">
          <div className="text-sm font-semibold">{online ? 'Connection is back' : 'Offline'}</div>
          <div className="truncate text-xs text-white/70">{user.firstName} · {syncedAt ? `patients from ${new Date(syncedAt).toLocaleString([], { hour: 'numeric', minute: '2-digit', month: 'short', day: 'numeric' })}` : 'no patients downloaded'}</div>
        </div>
        <button type="button" onClick={() => setView({ kind: 'sync' })} className="flex min-h-[44px] items-center gap-1 rounded-lg border border-white/30 px-3 text-sm font-semibold">
          <CloudOff size={16} aria-hidden="true" />{sync.pending > 0 ? `${sync.pending} not synced` : 'Sync'}
        </button>
        {online && (
          <button type="button" onClick={onReconnect} disabled={reconnecting} className="flex min-h-[44px] items-center gap-1 rounded-lg bg-white px-3 text-sm font-semibold text-slate-900 disabled:opacity-60">
            <RefreshCw size={16} className={reconnecting ? 'animate-spin' : ''} />Go online
          </button>
        )}
        <button type="button" onClick={onLock} aria-label="Lock" className="flex min-h-[44px] min-w-[44px] items-center justify-center rounded-lg border border-white/30">
          <Lock size={18} />
        </button>
      </header>

      {view.kind === 'sync' ? (
        <SyncCenter online={online} onClose={backToList} />
      ) : view.kind === 'workspace' && twoPane ? (
        <div className="grid grid-cols-[minmax(280px,1fr)_2fr]" data-testid="two-pane">
          <aside className="min-h-[calc(100vh-4rem)] border-r border-gray-200 px-4 py-4">{list}</aside>
          <section className="min-w-0">{workspace}</section>
        </div>
      ) : view.kind === 'workspace' ? (
        workspace
      ) : (
        <main className="mx-auto max-w-2xl px-4 py-4">
          <p className="mb-4 rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
            Offline. Start visits and chart here; your work is saved encrypted on this device and syncs when you&apos;re back online.
          </p>
          {list}
        </main>
      )}
    </div>
  );
};

export default OfflineHome;
